import type { ScreenPoint, ScreenRect } from '../types/screen-rect'
import { clampToLiveAreaX, clampToLiveAreaY } from './clamp-to-live-area'
import { rectsOverlap } from './rects-overlap'

const NUDGE_GAP = 12 // breathing room when nudging the toolbar clear of the shape

/**
 * When the manually-placed toolbar overlaps the shape's current bounds (e.g. while dragging a
 * node, or resizing the crop frame), nudge it just clear of the shape along whichever axis
 * requires the smallest move — keeping it as close as possible to where the user dragged it,
 * rather than jumping back to a fully recomputed "canonical" position. Returns manualPos
 * unchanged when there's no overlap. `width`/`height` are the toolbar's own approximate size.
 */
export function nudgeOutOfTheWay(
  manualPos: ScreenPoint,
  shapeRect: ScreenRect,
  width: number,
  height: number
): ScreenPoint {
  const toolbarRect = {
    left: manualPos.x,
    top: manualPos.y,
    right: manualPos.x + width,
    bottom: manualPos.y + height,
  }

  if (!rectsOverlap(shapeRect, toolbarRect)) return manualPos

  const pushRight = shapeRect.right - toolbarRect.left + NUDGE_GAP
  const pushLeft = toolbarRect.right - shapeRect.left + NUDGE_GAP
  const pushDown = shapeRect.bottom - toolbarRect.top + NUDGE_GAP
  const pushUp = toolbarRect.bottom - shapeRect.top + NUDGE_GAP
  const minPush = Math.min(pushRight, pushLeft, pushDown, pushUp)

  const clampX = (v: number) => clampToLiveAreaX(v, width)
  const clampY = (v: number) => clampToLiveAreaY(v, height)

  if (minPush === pushRight) return { x: clampX(manualPos.x + pushRight), y: manualPos.y }
  if (minPush === pushLeft) return { x: clampX(manualPos.x - pushLeft), y: manualPos.y }
  if (minPush === pushDown) return { x: manualPos.x, y: clampY(manualPos.y + pushDown) }

  return { x: manualPos.x, y: clampY(manualPos.y - pushUp) }
}
